/**
 * Hook-based Backend that lazily fetches fresh credentials per request.
 * If the token expired, getAccessTokenSilently() will automatically refresh
 * it before the request is made.
 */
import { useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AxiosRequestConfig } from 'axios';
import { Backend } from '../Services/Http/Backend';
import { Token } from '../Services/Security/Token';

export type LazyBackend = {
  get: <T = any>(path: string, params?: any, options?: AxiosRequestConfig) => Promise<T | undefined>;
  post: <T = any>(path: string, data?: any, options?: AxiosRequestConfig) => Promise<T>;
  put: <T = any>(path: string, data?: any, options?: AxiosRequestConfig) => Promise<T>;
  delete: <T = any>(path: string, params?: any, options?: AxiosRequestConfig) => Promise<T>;
};

export function useBackend(): LazyBackend {
  const { getAccessTokenSilently } = useAuth0();

  return useMemo(() => {
    const withFreshBackend = async <T>(fn: (backend: Backend) => Promise<T>): Promise<T> => {
      const accessToken = await getAccessTokenSilently();
      return fn(new Backend(new Token({ access_token: accessToken })));
    };

    return {
      get: <T>(path: string, params?: any, options?: AxiosRequestConfig) =>
        withFreshBackend((b) => b.get(path, params, options) as Promise<T | undefined>),

      post: <T>(path: string, data?: any, options?: AxiosRequestConfig) =>
        withFreshBackend((b) => b.post(path, data, options) as Promise<T>),

      put: <T>(path: string, data?: any, options?: AxiosRequestConfig) =>
        withFreshBackend((b) => b.put(path, data, options) as Promise<T>),

      delete: <T>(path: string, params?: any, options?: AxiosRequestConfig) =>
        withFreshBackend((b) => b.delete(path, params, options) as Promise<T>),
    };
  }, [getAccessTokenSilently]);
}
